import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import '../../../features/cards/css/creator/mediaGrid.css';
import { ImagesIcon } from '../../../assets/Icons/SVG';
import {
  MEDIA_TYPE,
  TAB_TYPE,
  PLACEHOLDER_POST_IMAGE
} from '../../../shared/constants/common';
import BundleSlider from '../../../features/slider/BundleSlider';
import LazyImage from '../../../components/LazyImage/LazyImage';
import VideoPlayer from '../../../shared/components/VideoPlayer';
import ViewContent from '../../../views/Modals/user/ViewContent';
import SubscribeModalLoggedIn from '../../../views/Modals/SubscribeModal/SubscribeModalLoggedIn';
import {
  formatDuration,
  isBundleContent,
  getMediaArray,
  getPrimaryMedia,
  getGradientBackgroundImage,
  isPostAccessible,
} from '../../../shared/utils/function';
import { getAmountWithExtraCharge } from '../../../shared/utils/paymentUtil';


const MediaGrid = ({
  tabType = TAB_TYPE.MEDIA,
  postId = null,
  index = null,
  post,
  userRole = null,
  isCreatorOwnProfile = false,
  creatorInfo = null,
  vatPercentage = 0,
  openDropdownId = null,
  onDropdownToggle = null,
  isShowDuration = false,
  showControls = false,
  hideSliderDots = false,
  isPPVContent = false,
  isSubContent = false,
  posts = [],
  currentIndex = 0,
  onPurchaseSuccess = null,
  onOpenFollowModal = null,
  onSubscriptionSuccess = null,
  // coming from mapStateToProps
  user = null,
}) => {
  // const isCreator = userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT;
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentPostIndex, setCurrentPostIndex] = useState(currentIndex);
  const [showPostModal, setShowPostModal] = useState(false);
  const subscribeModalTriggerRef = useRef({ onClick: null, isDisabled: false });

  const isSubscribed = isPostAccessible({
    post,
    isPPVContent,
    isSubContent,
    creatorInfo,
  });

  // Update currentPostIndex when currentIndex prop changes
  useEffect(() => {
    setCurrentPostIndex(currentIndex);
  }, [currentIndex]);

  // Memoized bundle content detection
  const isBundle = useMemo(() => isBundleContent(post), [
    post.assets,
    post.type_file,
    post.type_content,
    post.content_type,
    post.isBundle
  ]);

  // Memoized media array for bundle content
  const mediaArray = useMemo(() => {
    return getMediaArray(post) || post.media || null;
  }, [
    post.assets,
    post.type_file,
    post.type_content,
    post.content_type,
    post.isBundle,
    post.media,
    post.caption
  ]);

  const primaryMedia = useMemo(() => getPrimaryMedia(post), [post.assets, post.cover_index, post.type_file]);

  // Memoized media statistics
  const stats = useMemo(() => {
    const mediaToCount = mediaArray || post.media || [];
    if (!mediaToCount.length) {
      return { imageCount: 0, videoCount: 0, totalDuration: '' };
    }

    const imageCount = mediaToCount.filter(m => m.type_file === MEDIA_TYPE.IMAGE).length;
    const videoCount = mediaToCount.filter(m => m.type_file === MEDIA_TYPE.VIDEO).length;
    const totalDuration = mediaToCount.reduce((total, m) => total + (m.duration || 0), 0);

    return {
      imageCount,
      videoCount,
      totalDuration: formatDuration(totalDuration)
    };
  }, [mediaArray, post.media]);

  const onView = useCallback((post) => {
    setSelectedPost(post);
    setCurrentPostIndex(currentIndex);
    setShowPostModal(true);
  }, [currentIndex]);

  const handleClosePostModal = useCallback(() => {
    setSelectedPost(null);
    setShowPostModal(false);
  }, []);

  const handleItemClick = useCallback(() => {
    onView?.(post);
  }, [onView, post]);

  // Handle open subscribe modal
  const handleOpenSubscribeModal = useCallback(() => {
    setSelectedPost(null);
    setShowPostModal(false);
    const { onClick, isDisabled } = subscribeModalTriggerRef.current || {};
    if (!isDisabled && typeof onClick === 'function') {
      onClick();
    }
  }, []);

  // Handle open follow modal
  const handleOpenFollowModal = useCallback(() => {
    setSelectedPost(null);
    setShowPostModal(false);
    if (onOpenFollowModal) {
      onOpenFollowModal();
    }
  }, []);

  // Generate unique gradient seed for this feed item
  const gradientSeed = useMemo(() => {
    return postId || index || post._id || null;
  }, [postId, index, post._id]);

  return (
    <div className="creator-feed-item-wrapper" data-post-id={postId}>
      <div
        className="creator-feed-item"
        onClick={handleItemClick}
      >
        {/* Render bundle content with slider or single image/video */}
        <div className='creator-feed-item-content'>
          {isBundle && mediaArray?.length > 0 ? (
            <BundleSlider
              media={mediaArray}
              className="creator-feed-bundle-slider"
              showControls={showControls}
              isLock={!isSubscribed}
              isShowDuration={isShowDuration}
              hideSliderDots={hideSliderDots}
              gradientSeed={gradientSeed}
            />
            // <div>demo</div>
          ) :
            isSubscribed ? (
              post.type_file === MEDIA_TYPE.VIDEO ? (
                <VideoPlayer
                  src={primaryMedia.src}
                  poster={primaryMedia.poster || null}
                  className={`creator-feed-video hide_video_controls video-no-timeline ${post.lock_content ? 'video-locked' : ''}`}
                  showControls={showControls}
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  disableRemotePlayback
                  preventContextMenu
                  muted
                  loop
                  playsInline
                  preload="none"
                  fallbackText='Video unavailable'
                  showDurationBadge={!post.lock_content}
                />
              ) : (
                <LazyImage
                  src={primaryMedia.src}
                  alt={post.caption || 'Content'}
                  onError={(e) => { e.target.src = PLACEHOLDER_POST_IMAGE; }}
                />
              )
            ) : (
              <div className="media-grid-locked-content">
                <div className='cm_blur_16'></div>
                <img
                  alt="..."
                  src={getGradientBackgroundImage(currentIndex)} />
              </div>
            )}
        </div>

        {/* Trending icon for trending content & PPV content */}
        {isPPVContent && post.isTrending && post.isTrending === true && (
          <div className='cm_fire_tag_wrap'>
            <i className="fa-solid fa-fire"></i>
          </div>
        )}

        {/* Lock icon overlay - shown for all locked content */}
        {!isSubscribed && (
          <div className={`creator-feed-lock-icon ${post?.price ? 'with_amount' : ''}`}>
            <i className='fas fa-lock'></i>
            {isPPVContent && (
              <span>
                ${getAmountWithExtraCharge(post?.price, vatPercentage)}
              </span>
            )}
          </div>
        )}

        {/* Media stats */}
        {isBundle && mediaArray?.length > 0 && (
          <div className="creator-feed-stats">
            <div className="creator-feed-stats-content">
              {stats.imageCount > 0 && (
                <>
                  {tabType === TAB_TYPE.MEDIA && (
                    <span>{stats.imageCount}</span>
                  )}

                  {tabType === TAB_TYPE.MEDIA && <i className="fa-solid fa-image"></i>}
                  {tabType === TAB_TYPE.FREE &&
                    <>
                      {stats.imageCount > 1 ? (
                        <ImagesIcon />
                      ) : (
                        <i className="fa-solid fa-image"></i>
                      )}
                    </>
                  }
                </>
              )}
              {stats.videoCount > 0 && (
                <>
                  {stats.imageCount > 0 && <span>•</span>}
                  {tabType === TAB_TYPE.MEDIA && (
                    <span>{stats.videoCount}</span>
                  )}
                  <i className="fa-solid fa-video"></i>

                  {stats.totalDuration && (
                    <>
                      <span>•</span>
                      <span>{stats.totalDuration}</span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Single media stats */}
        {!isBundle && (post.type_file === MEDIA_TYPE.VIDEO || post.type_file === MEDIA_TYPE.IMAGE) && (
          <div className="creator-feed-stats">
            <div className="creator-feed-stats-content">
              {post.type_file === MEDIA_TYPE.IMAGE && (
                <>
                  <i className="fa-solid fa-image"></i>
                  {/* {tabType === TAB_TYPE.MEDIA && <i className="fa-solid fa-image"></i>}
                  {tabType === TAB_TYPE.FREE && <i className="fa-solid fa-camera"></i>} */}
                </>
              )}
              {post.type_file === MEDIA_TYPE.VIDEO && (
                <>
                  <i className="fa-solid fa-video"></i>
                  {post.duration && <span>{formatDuration(post.duration)}</span>}
                </>
              )}
            </div>
          </div>
        )}

        {/* Gradient overlay */}
        {/* {!isSubscribed && ( */}
          <div className="creator-feed-gradient-overlay"></div>
        {/* )} */}
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <ViewContent
          handleClosePostModal={handleClosePostModal}
          post={selectedPost}
          creatorInfo={creatorInfo || {}}
          creatorRole={creatorInfo?.rol}
          isCreatorOwnProfile={isCreatorOwnProfile}
          openDropdownId={openDropdownId}
          onDropdownToggle={onDropdownToggle}
          isDarkMode={true}
          showControls={true}
          isShowDuration={true}
          isShowChatButton={false}
          isShowEllipsis={true}
          isPPVContent={isPPVContent}
          isSubContent={isSubContent}
          posts={posts}
          currentIndex={currentPostIndex}
          onNavigate={(newIndex) => {
            if (newIndex >= 0 && newIndex < posts.length) {
              setCurrentPostIndex(newIndex);
              setSelectedPost(posts[newIndex]);
            }
          }}
          onUnlockClick={onPurchaseSuccess}
          onOpenSubscribeModal={handleOpenSubscribeModal}
          onOpenFollowModal={handleOpenFollowModal}
          vatPercentage={vatPercentage}
        />
      )}

      {/* Subscribe Modal */}
      {creatorInfo && (
        <SubscribeModalLoggedIn
          creatorInfo={creatorInfo}
          handleCallback={onSubscriptionSuccess}
          renderTrigger={({ onClick, isDisabled }) => {
            subscribeModalTriggerRef.current = { onClick, isDisabled };
            return null;
          }}
        />
      )}
    </div>
  );
};

MediaGrid.propTypes = {
  postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  posts: PropTypes.array, // Array of all posts
  currentIndex: PropTypes.number, // Current post index
  post: PropTypes.shape({
    type_file: PropTypes.string,
    type_content: PropTypes.string,
    content_type: PropTypes.string,
    isBundle: PropTypes.bool,
    caption: PropTypes.string,
    isTrending: PropTypes.bool,
    assets: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      type: PropTypes.string,
      duration: PropTypes.number,
    })),
  }).isRequired,
  userRole: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCreatorOwnProfile: PropTypes.bool,
  creatorInfo: PropTypes.object,
  vatPercentage: PropTypes.number,
  tabType: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  user: PropTypes.shape({
    _id: PropTypes.string,
    rol: PropTypes.number,
  }),
  openDropdownId: PropTypes.string,
  onDropdownToggle: PropTypes.func,
  isShowDuration: PropTypes.bool,
  showControls: PropTypes.bool,
  hideSliderDots: PropTypes.bool,
  isPPVContent: PropTypes.bool,
  isSubContent: PropTypes.bool,
  onPurchaseSuccess: PropTypes.func,
  onOpenFollowModal: PropTypes.func,
  onSubscriptionSuccess: PropTypes.func,
};

const mapStateToProps = (state) => ({
  user: state.user?.user,
});

export default connect(mapStateToProps)(MediaGrid);
